import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { apiService } from '../services/apiService'; // Adjust the import path
import { setUser } from '../Redux/userSlice';

const StripeCheckoutButton = ({ planId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const toast = useRef(null);
  const user = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Map planId to amount (in cents) and credits
  const planDetails = {
    1: { amount: 500, credits: 1 }, // $5.00 for 1 Credit
    2: { amount: 1200, credits: 3 }, // $12.00 for 3 Credits
    3: { amount: 2000, credits: 5 }, // $20.00 for 5 Credits
  };

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail, life: 3000 });
  };

  const handleCheckout = async () => {
    if (!stripe || !elements) {
      setError('Stripe initialization failed. Please check your Stripe configuration or try again later.');
      showToast('error', 'Error', 'Stripe initialization failed. Please check your Stripe configuration or try again later.');
      return;
    }

    if (!user.email) {
      setError('Please log in to make a payment.');
      showToast('error', 'Error', 'Please log in to make a payment.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call backend to create Payment Intent
      const response = await apiService.createPaymentIntent({
        email: user.email,
        amount: planDetails[planId].amount,
      });

      const { clientSecret } = response;
      if (!clientSecret || typeof clientSecret !== 'string') {
        throw new Error('Invalid or missing clientSecret in response');
      }

      // Get the CardElement
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm the payment with Stripe
      const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: user.email,
            name: user.name || 'Anonymous',
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        showToast('error', 'Payment Failed', stripeError.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Fetch updated credits
        const creditsResponse = await apiService.getCredits(user.email);
        dispatch(setUser({ credits: creditsResponse.body.credits }));
        showToast('success', 'Success', `Payment successful! Added ${planDetails[planId].credits} credit(s).`);
        if (onSuccess) onSuccess(); // Close the modal
      }
    } catch (err) {
      const errorMessage = err.message.includes('Creating payment intent failed')
        ? err.message
        : `Payment failed: ${err.message}`;
      setError(errorMessage);
      showToast('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <CardElement
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
          hidePostalCode: true,
          disableLink: true,
          wallets: {
            applePay: 'never',
            googlePay: 'never',
          },
        }}
      />
      <Button
        label={loading ? 'Processing...' : 'Pay Now'}
        icon="pi pi-credit-card"
        className="p-button-raised p-button-success mt-2"
        onClick={handleCheckout}
        disabled={!stripe || !user.email || loading}
      />
      {error && <p className="p-error mt-2">{error}</p>}
    </div>
  );
};

export default StripeCheckoutButton;